/**
 * v4.3.1 — Demo Context (the Guided Demo state machine).
 * v4.8.2 — Decoupled from auto-launch. Guided Demo no longer persists
 * across sessions. Tour state lives in memory for the lifetime of the
 * tab and is fully cleared on mount so a stale sessionStorage key from
 * a previous deployment never auto-resumes the tour. The Guided Demo
 * is now an OPT-IN presentation layer launched explicitly from inside
 * Master Demo Mode — never from a persisted flag.
 *
 * One context lives at the OperationalShell root. It carries:
 *   * isActive            — true when a tour is running
 *   * persona             — full manifest loaded from the backend
 *   * step                — current tour stop index (0..tour.length-1)
 *   * mode                — "interactive" | "passive" (auto-advance)
 *   * start(personaKey)   — begin the tour, navigate to stop[0].route
 *   * stop()              — exit the tour cleanly
 *   * next() / prev()     — manual stepping
 *   * setMode(mode)
 *
 * NOT persisted to storage. Reactively re-fetches the persona manifest
 * whenever the language changes (so coach copy stays consistent across
 * i18n switches).
 */
import React, {
    createContext, useCallback, useContext, useEffect, useMemo,
    useRef, useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../../lib/api";

const LEGACY_STORAGE_KEY = "ob.demo_session";
const PASSIVE_INTERVAL_MS = 12000;

const DemoCtx = createContext({
    isActive: false,
    persona: null,
    step: 0,
    mode: "interactive",
    completed: false,
    start: () => {},
    stop: () => {},
    next: () => {},
    prev: () => {},
    setMode: () => {},
    restart: () => {},
    dismissCompletion: () => {},
});

// v4.8.2 — On first mount, purge any stale persisted session from prior
// deployments so the tour never auto-resumes. Safe no-op if absent.
const purgeLegacySession = () => {
    try { sessionStorage.removeItem(LEGACY_STORAGE_KEY); }
    catch (_e) { /* noop */ }
};

export const DemoProvider = ({ children }) => {
    const navigate = useNavigate();
    const { i18n } = useTranslation();
    const lang = (i18n.language || "en").split("-")[0];

    const [isActive, setIsActive] = useState(false);
    const [personaKey, setPersonaKey] = useState(null);
    const [persona, setPersona] = useState(null);
    const [step, setStep] = useState(0);
    const [mode, setMode] = useState("interactive");
    const [completed, setCompleted] = useState(false);
    const passiveTimer = useRef(null);

    // Run once on mount: clear any legacy persisted tour state.
    useEffect(() => {
        purgeLegacySession();
    }, []);

    // Whenever isActive + personaKey + lang change, re-fetch the
    // manifest so coach copy stays in step with the chosen language.
    useEffect(() => {
        if (!isActive || !personaKey) {
            setPersona(null);
            return;
        }
        let cancelled = false;
        api.get(`/operational-base/demo/personas/${personaKey}?lang=${lang}`)
            .then((res) => { if (!cancelled) setPersona(res.data); })
            .catch(() => {
                // Persona fetch failed — exit the tour so the operator
                // isn't stuck with "demo active" but no coach card or
                // entry points.
                if (cancelled) return;
                setPersona(null);
                setIsActive(false);
                setPersonaKey(null);
            });
        return () => { cancelled = true; };
    }, [isActive, personaKey, lang]);

    // v4.8.2 — Tour state intentionally NOT persisted. The Guided Demo
    // is an opt-in presentation layer; it should never survive a tab
    // refresh or auto-resume on a future login.

    const goToStep = useCallback((index) => {
        if (!persona) return;
        const clamped = Math.max(0, Math.min(persona.tour.length - 1, index));
        const stop = persona.tour[clamped];
        if (!stop) return;
        setStep(clamped);
        if (stop.route) navigate(stop.route);
    }, [persona, navigate]);

    const start = useCallback((key = "blue_flame", startMode = "interactive") => {
        setPersonaKey(key);
        setMode(startMode);
        setIsActive(true);
        setStep(0);
        setCompleted(false);
        // Persona fetch is async — landing route applied below via effect.
    }, []);

    // When persona resolves and we're on step 0, route to the first stop.
    const lastRoutedKey = useRef(null);
    useEffect(() => {
        if (!isActive || !persona) return;
        const sig = `${persona.key}#${step}`;
        if (lastRoutedKey.current === sig) return;
        lastRoutedKey.current = sig;
        const stop = persona.tour[step];
        if (stop?.route) navigate(stop.route);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [persona, isActive, step]);

    const stop = useCallback(() => {
        setIsActive(false);
        setPersona(null);
        setPersonaKey(null);
        setStep(0);
        setCompleted(false);
        if (passiveTimer.current) clearTimeout(passiveTimer.current);
    }, []);

    // v4.8.3 — Executive-grade tour completion. Instead of dissolving
    // the tour on step 8, we surface a calm completion card that lets
    // the operator re-launch, exit Demo Mode, or continue exploring.
    const markCompleted = useCallback(() => {
        setCompleted(true);
        if (passiveTimer.current) clearTimeout(passiveTimer.current);
    }, []);

    const dismissCompletion = useCallback(() => {
        // Close the completion card and the tour overlay; Demo Mode
        // remains active. The operator stays on whatever route they
        // were last guided to and can explore freely.
        setCompleted(false);
        setIsActive(false);
        setPersona(null);
        setPersonaKey(null);
        setStep(0);
    }, []);

    const restart = useCallback(() => {
        if (!personaKey) {
            // Fallback: re-arm the Blue Flame tour from scratch.
            start("blue_flame", "interactive");
            return;
        }
        setCompleted(false);
        setStep(0);
        // Persona is already loaded; the routing effect below will
        // re-navigate to the first stop because we reset `step` to 0.
    }, [personaKey, start]);

    const next = useCallback(() => {
        if (!persona) return;
        if (step + 1 >= persona.tour.length) {
            // v4.8.3 — Surface the calm completion card instead of
            // dissolving the tour.
            markCompleted();
            return;
        }
        goToStep(step + 1);
    }, [persona, step, goToStep, markCompleted]);

    const prev = useCallback(() => { goToStep(step - 1); }, [goToStep, step]);

    // Passive mode auto-advance.
    useEffect(() => {
        if (passiveTimer.current) clearTimeout(passiveTimer.current);
        if (!isActive || mode !== "passive" || !persona || completed) return;
        passiveTimer.current = setTimeout(next, PASSIVE_INTERVAL_MS);
        return () => { if (passiveTimer.current) clearTimeout(passiveTimer.current); };
    }, [isActive, mode, persona, step, next, completed]);

    const value = useMemo(() => ({
        isActive, persona, step, mode, completed,
        start, stop, next, prev, setMode,
        restart, dismissCompletion,
    }), [
        isActive, persona, step, mode, completed,
        start, stop, next, prev, restart, dismissCompletion,
    ]);

    return <DemoCtx.Provider value={value}>{children}</DemoCtx.Provider>;
};

export const useDemo = () => useContext(DemoCtx);
export default DemoProvider;
